import styled from 'styled-components/macro'
import posed from 'react-pose'
import { theme } from 'styled-tools'
import { CircleIcon as BaseCircleIcon } from '../../../ui/components/CircleIcons'
import { media } from '../../../ui/rwd'
import { Link as BaseLink } from 'react-router-dom'

const AnimatedContainer = posed.div({
  visible: {
    opacity: 1,
    y: 0,
    transition: ({ delay = 0 }) => ({
      type: 'spring',
      stiffness: 150,
      damping: 15,
      delay: 300 + delay
    })
  },

  hidden: {
    opacity: 0,
    y: 100
  }
})

const MainContainer = styled(AnimatedContainer)`
  background-color: ${theme('black')};
  width: 100%;
  flex: 1 1 100%;
  max-width: 100%;
  font-size: 1.8rem;
`

const FooterInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 1.25rem;

  ${media.md`
    padding: 0 2.5rem;
  `}
`

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;
  padding: 2.25rem 0 1.5rem;

  ${media.sm`
    grid-template-columns: 1fr 1fr;
    gap: 2rem 3rem;
    padding: 2.75rem 0 1.75rem;
  `}

  ${media.md`
    grid-template-columns: minmax(220px, 1.2fr) minmax(280px, 1.4fr) minmax(160px, 0.9fr);
    gap: 2.5rem 4rem;
    align-items: start;
    padding: 3.25rem 0 2rem;
  `}
`

const BrandColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${media.sm`
    grid-column: 1 / -1;
  `}

  ${media.md`
    grid-column: auto;
  `}
`

const Logo = styled.img`
  width: 140px;
  height: auto;
  margin-bottom: 0.75rem;

  ${media.md`
    width: 168px;
    margin-bottom: 1rem;
  `}
`

const Description = styled.p`
  max-width: 36rem;
  color: ${theme('lightGray')};
  font-size: 0.55em;
  line-height: 1.55;
  margin: 0;
`

const LinksCluster = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem 1.25rem;

  ${media.md`
    gap: 1.5rem 2.5rem;
  `}
`

const LinkColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const ColumnTitle = styled.h3`
  color: ${theme('white')};
  font-size: 0.58em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin: 0 0 0.55rem;
`

const LinkFooter = styled(BaseLink)`
  display: block;
  font-size: 0.52em;
  color: ${theme('lightGray')};
  margin-bottom: 0.35rem;
  &:hover {
    color: white;
    text-decoration: underline;
  }
`

const LinkFooterExternal = styled.a`
  display: block;
  font-size: 0.52em;
  color: ${theme('lightGray')};
  margin-bottom: 0.35rem;
  &:hover {
    color: white;
    text-decoration: underline;
  }
`

const DownloadColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const AppGroupsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
  width: 100%;
`

const AppGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const AppGroupLabel = styled.span`
  color: ${theme('lightGray')};
  font-size: 0.42em;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.4rem;
`

const StoresLink = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
`

const AppLink = styled.a`
  cursor: pointer;
  display: inline-flex;
  line-height: 0;
`

const AppMobileIcon = styled.img`
  width: 135px;
  height: auto;
  display: block;

  ${media.md`
    width: 148px;
  `}
`

const ShopifyLink = styled.a`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 135px;
  height: 40px;
  padding: 0 10px;
  background: #000;
  border-radius: 8px;
  color: ${theme('white')};
  text-decoration: none;
  line-height: 1;
  &:hover {
    color: ${theme('white')};
    text-decoration: none;
    background: #111;
  }
  svg {
    width: 22px;
    height: 22px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  ${media.md`
    width: 148px;
    height: 44px;
    svg {
      width: 24px;
      height: 24px;
    }
  `}
`

const ShopifyCopy = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-width: 0;
`

const ShopifyEyebrow = styled.span`
  font-size: 8px;
  font-weight: 400;
  letter-spacing: 0.02em;
  line-height: 1.1;
  white-space: nowrap;

  ${media.md`
    font-size: 9px;
  `}
`

const ShopifyName = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.15;
  white-space: nowrap;

  ${media.md`
    font-size: 15px;
  `}
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1.1rem 0 1.4rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  ${media.sm`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  `}
`

const LegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.4rem 0.8rem;

  a {
    margin-bottom: 0;
  }
`

const LegalDot = styled.span`
  color: ${theme('lightGray')};
  font-size: 0.5em;
`

const SocialRow = styled.div`
  display: flex;
  align-items: center;
`

const SocialLink = styled.a`
  margin-right: 0.8rem;
  &:last-child {
    margin-right: 0;
  }
`

const CircleIcon = styled(BaseCircleIcon).attrs({
  width: '2.25rem',
  height: '2.25rem'
})`
  cursor: pointer;
  background: ${theme('lightGray')};
  color: ${theme('black')};
  padding: 0;
  &:hover {
    background: white;
  }

  svg {
    width: 1.05rem;
    height: 1.05rem;
  }
`

const LangPickerWrap = styled.div`
  button,
  .dropdown-toggle {
    color: ${theme('lightGray')} !important;
    font-size: 1rem !important;
  }

  .dropdown-toggle::after {
    display: none !important;
  }
`

export {
  MainContainer,
  FooterInner,
  TopSection,
  BrandColumn,
  Logo,
  Description,
  LinksCluster,
  LinkColumn,
  ColumnTitle,
  LinkFooter,
  LinkFooterExternal,
  DownloadColumn,
  AppGroup,
  AppGroupsGrid,
  AppGroupLabel,
  StoresLink,
  AppLink,
  AppMobileIcon,
  ShopifyLink,
  ShopifyCopy,
  ShopifyEyebrow,
  ShopifyName,
  BottomSection,
  LegalLinks,
  LegalDot,
  SocialRow,
  SocialLink,
  CircleIcon,
  LangPickerWrap
}
